/* eslint-disable arrow-body-style */

import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  FormArray,
  Validators,
  FormControl,
  AbstractControl,
  ValidatorFn,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import * as validatorUtility from '@cigna/cigna-for-brokers/shared/util';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { AppstateFacade } from '@cigna/cigna-for-brokers/shell/data-access';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'cigna-cfb-contractor-address-form',
  templateUrl: './contractor-address-form.component.html',
  styleUrls: ['./contractor-address-form.component.scss'],
})
export class ContractorAddressFormComponent implements OnInit {
  contractorAddressForm: FormGroup;
  physicalAddressIndex = 0;
  mailingAddressIndex = 1;
  isOnlyPhysicalAddress = true;
  shouldShowMailingAddressForm = false;
  countries: string[] = [];
  @Output() cancelClicked = new EventEmitter();
  @Output() continueClicked = new EventEmitter();
  @Output() mailAddressRequested = new EventEmitter<boolean>();
  respClass = 'address-responsive';
  selectedState = this._translate.instant('global.select');
  getFormData$ = this.appstateFacade.getOnBoardingFormData$;
  _ngSubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private _translate: TranslateService,
    private matDialog: MatDialog,
    private appstateFacade: AppstateFacade,
  ) {
    const countriesJson = this._translate.instant(
      'onboarding.addressInfo.stateList',
    ) as string[];
    this.countries = [...countriesJson].sort();
  }

  ngOnInit() {
    this.contractorAddressForm = this.formBuilder.group({
      addresses: new FormArray([]),
    });
    this.addressForms.reset();

    this.addressForms.push(
      this.formBuilder.group({
        street1: ['', [Validators.required, Validators.minLength(2)]],
        street2: ['', Validators.minLength(2)],
        city: ['', [Validators.required, Validators.minLength(2)]],
        state: [
          '',
          [Validators.required, validatorUtility.selectRequiredValidator],
        ],
        zipcode: ['', [Validators.required, Validators.pattern('^\\d{5}$')]],
      }),
    );

    this.appstateFacade.sendAnalytics({
      event: 'pageLoad',
      reqData: {
        requirementId: 'pvONB002',
        userStory: 'MTD-3577',
        hitType: 'page view',
      },
      pageData: {
        siteSection: 'Onboarding',
        URL: window.location.href,
        pageName: 'Address Information',
      },
    });

    this.getFormData$.pipe(takeUntil(this._ngSubscribe$)).subscribe((res) => {
      this.addressFormGroups[this.physicalAddressIndex].patchValue({
        street1: res.addressInfo?.physicalAddress?.street1,
        street2: res.addressInfo?.physicalAddress?.street2,
        city: res.addressInfo?.physicalAddress?.city,
        state: res.addressInfo?.physicalAddress?.state,
        zipcode: res.addressInfo?.physicalAddress?.zipCode,
      });

      if (res.addressInfo) {
        this.isOnlyPhysicalAddress = res.addressInfo?.isSameAsPhysical;
      }

      if (res.addressInfo?.isSameAsPhysical === false) {
        this.addressForms.push(
          this.formBuilder.group({
            mastreet1: ['', [Validators.required, Validators.minLength(2)]],
            mastreet2: ['', Validators.minLength(2)],
            macity: ['', [Validators.required, Validators.minLength(2)]],
            state: [
              '',
              [Validators.required, validatorUtility.selectRequiredValidator],
            ],
            mazipcode: [
              '',
              [Validators.required, Validators.pattern('^\\d{5}$')],
            ],
          }),
        );

        this.shouldShowMailingAddressForm = true;

        this.addressFormGroups[this.mailingAddressIndex].patchValue({
          mastreet1: res.addressInfo?.mailingAddress?.street1,
          mastreet2: res.addressInfo?.mailingAddress?.street2,
          macity: res.addressInfo?.mailingAddress?.city,
          state: res.addressInfo?.mailingAddress?.state,
          mazipcode: res.addressInfo?.mailingAddress?.zipCode,
        });
      }
    });
  }

  get addressForms() {
    return this.contractorAddressForm.controls['addresses'] as FormArray;
  }
  get addressFormGroups() {
    return this.addressForms.controls as FormGroup[];
  }

  onlyPhysicalAddressChecked(isOnlyPhysicalAddressChecked: boolean) {
    this.isOnlyPhysicalAddress = isOnlyPhysicalAddressChecked;

    if (!isOnlyPhysicalAddressChecked) {
      this.addressForms.push(
        this.formBuilder.group({
          mastreet1: ['', [Validators.required, Validators.minLength(2)]],
          mastreet2: ['', Validators.minLength(2)],
          macity: ['', [Validators.required, Validators.minLength(2)]],
          state: [
            '',
            [Validators.required, validatorUtility.selectRequiredValidator],
          ],
          mazipcode: [
            '',
            [Validators.required, Validators.pattern('^\\d{5}$')],
          ],
        }),
      );
      this.shouldShowMailingAddressForm = true;
      setTimeout(() => this.mailAddressRequested.emit(true), 0);
    } else {
      this.addressForms.removeAt(this.mailingAddressIndex);
      this.shouldShowMailingAddressForm = false;
      this.mailAddressRequested.emit(false);
    }
  }

  get street1() {
    return this.contractorAddressForm.get('street1');
  }

  onContinue() {
    const addressData = {
      physicalAddress: {
        street1: this.addressFormGroups[0].get('street1')?.value ?? '',
        street2: this.addressFormGroups[0].get('street2')?.value ?? '',
        city: this.addressFormGroups[0].get('city')?.value ?? '',
        state: this.addressFormGroups[0].get('state')?.value ?? '',
        zipCode: this.addressFormGroups[0].get('zipcode')?.value ?? '',
      },
      mailingAddress: {
        street1: this.isOnlyPhysicalAddress
          ? this.addressFormGroups[0].get('street1')?.value
          : this.addressFormGroups[1]?.get('mastreet1')?.value,
        street2: this.isOnlyPhysicalAddress
          ? this.addressFormGroups[0].get('street2')?.value
          : this.addressFormGroups[1]?.get('mastreet2')?.value,
        city: this.isOnlyPhysicalAddress
          ? this.addressFormGroups[0].get('city')?.value
          : this.addressFormGroups[1]?.get('macity')?.value,
        state: this.isOnlyPhysicalAddress
          ? this.addressFormGroups[0].get('state')?.value
          : this.addressFormGroups[1]?.get('state')?.value,
        zipCode: this.isOnlyPhysicalAddress
          ? this.addressFormGroups[0].get('zipcode')?.value
          : this.addressFormGroups[1]?.get('mazipcode')?.value,
      },
      isSameAsPhysical: this.isOnlyPhysicalAddress,
    };
    this.appstateFacade.saveAddress(addressData);

    this.continueClicked.emit();
  }

  numberOnly($event: { which: number; keyCode: number }): boolean {
    return validatorUtility.numberOnly($event);
  }

  cancelForm() {
    this.cancelClicked.emit();
  }

  selectRequiredValidator(ctrl: AbstractControl): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      return control.value ? null : { required: { value: control.value } };
    };
  }
}
